import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, ImageIcon } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ImageMarquee from "@/components/ImageMarquee";

export default function LandingPage() {
  const [event, setEvent] = useState(null);
  const [moodboardUrl, setMoodboardUrl] = useState(null);
  const [seats, setSeats] = useState({ claimed: 0, total: 20 });

  useEffect(() => {
    api.get("/event").then((r) => setEvent(r.data)).catch(() => {});
    api.get("/moodboard").then((r) => setMoodboardUrl(r.data?.url || null)).catch(() => {});
    api.get("/seats").then((r) => setSeats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <SiteHeader />

      {/* Hero heading — full width, horizontal, centered */}
      <section className="container-kynn pt-20 pb-16 text-center">
        <h1
          className="font-display fade-up"
          style={{
            fontSize: "clamp(44px, 6.8vw, 118px)",
            lineHeight: "1.02",
            color: "#E2D3E2",
            letterSpacing: "-0.025em",
          }}
          data-testid="landing-hero-heading"
        >
          <span style={{ whiteSpace: "nowrap" }}>
            A <em style={{ fontStyle: "italic", color: "#69254F" }}>Business</em> Book Club
          </span>
          <br />
          <span style={{ whiteSpace: "nowrap" }}>&amp; Picnic Pop-Up</span>
        </h1>
      </section>

      {/* Meta row — centered */}
      <section className="container-kynn pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 fade-up max-w-3xl mx-auto text-center" style={{ animationDelay: "260ms" }}>
          <MetaBlock label="when" value={event?.date || "Friday, August 14th, 5-8PM"} />
          <MetaBlock
            label="where"
            value="Venice, CA"
            note="address shared upon approval"
          />
        </div>
      </section>

      {/* Floating marquee — sits between the meta row and the RSVP CTA */}
      <ImageMarquee />

      {/* Centered RSVP CTA — above moodboard */}
      <section className="container-kynn pt-12 pb-16 flex flex-col items-center gap-6">
        <Link
          to="/rsvp"
          data-testid="rsvp-here-btn"
          className="btn-primary fade-up"
          style={{
            padding: "22px 56px",
            fontSize: "13px",
            borderRadius: "8px",
            animationDelay: "340ms",
          }}
        >
          RSVP here <ArrowRight size={14} />
        </Link>

        <SeatsBar claimed={seats.claimed} total={seats.total} />
      </section>

      {/* Evening moodboard — 16:9 horizontal */}
      <section className="container-kynn pb-24">
        <div
          className="small-caps mb-4 text-center"
          style={{ color: "#B9929D" }}
          data-testid="moodboard-label"
        >
          Evening Moodboard
        </div>
        <div
          className="w-full overflow-hidden"
          style={{
            aspectRatio: "16 / 9",
            background: "transparent",
            borderRadius: "4px",
          }}
          data-testid="moodboard-slot"
        >
          {moodboardUrl ? (
            <img
              src={moodboardUrl}
              alt="Evening moodboard"
              className="w-full h-full object-cover"
              data-testid="moodboard-image"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
              <ImageIcon size={32} style={{ color: "#DDBFCF", opacity: 0.5 }} />
              <p className="small-caps" style={{ color: "#DDBFCF", opacity: 0.6 }}>
                Moodboard coming soon
              </p>
              <p className="text-xs italic" style={{ color: "#DDBFCF", opacity: 0.4 }}>
                The host can upload an image from the dashboard.
              </p>
            </div>
          )}
        </div>

        {/* Moodboard notes — left aligned */}
        <div
          className="mt-10 max-w-2xl space-y-5 text-left"
          style={{ color: "#F2EEE7", lineHeight: 1.75 }}
          data-testid="moodboard-notes"
        >
          <p>
            <span className="small-caps" style={{ color: "#b8c975", marginRight: 10 }}>
              What to read beforehand
            </span>
            <br />
            <em style={{ color: "#DDBFCF" }}>Start With Yourself</em> by Emma Grede{" "}
            <a
              href="https://www.amazon.com/dp/1668085488/?coliid=I1M1VJJ7JF50T5&colid=1FHI53EOVTLE3&psc=1&ref_=cm_sw_r_cp_ud_lstpd_SH4NY0H6RV9QR2DM1BR8_1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-link"
              style={{
                color: "#b8c975",
                borderBottom: "1px solid rgba(184,201,117,0.5)",
                paddingBottom: "1px",
              }}
            >
              link here
            </a>
          </p>
          <p>
            <span className="small-caps" style={{ color: "#b8c975", marginRight: 10 }}>
              What to expect
            </span>
            <br />
            Guided discussion questions &amp; activities while enjoying drinks &amp; hors d&rsquo;oeuvres with other entrepreneurial women.
          </p>
          <p>
            <span className="small-caps" style={{ color: "#b8c975", marginRight: 10 }}>
              What to bring
            </span>
            <br />
            Yourself <span style={{ color: "#DDBFCF" }}>♡</span> and the willingness to be open and honest{" "}
            <span style={{ color: "#DDBFCF", whiteSpace: "nowrap" }}>˖ ࣪ ⊹</span>
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function MetaBlock({ label, value, note }) {
  return (
    <div>
      <div
        className="font-scribble mb-1"
        style={{ color: "#b8c975", fontSize: "30px", lineHeight: 1 }}
      >
        {label}
      </div>
      <div
        className="font-mono-body"
        style={{ fontSize: "13px", color: "#F2EEE7", lineHeight: 1.5, letterSpacing: "0.02em" }}
      >
        {value}
      </div>
      {note && (
        <div
          className="font-mono-body italic mt-1"
          style={{
            fontSize: "10px",
            color: "#DDBFCF",
            opacity: 0.7,
            letterSpacing: "0.04em",
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
}

function SeatsBar({ claimed, total }) {
  const pct = Math.min(100, total > 0 ? (claimed / total) * 100 : 0);
  return (
    <div className="w-full max-w-xs fade-up" style={{ animationDelay: "420ms" }} data-testid="seats-bar">
      <div
        className="small-caps text-center mb-3"
        style={{ color: "#DDBFCF", opacity: 0.7 }}
        data-testid="seats-label"
      >
        {claimed} of {total} seats claimed
      </div>
      <div
        style={{
          height: "3px",
          width: "100%",
          background: "rgba(221, 191, 207,0.18)",
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        <div
          data-testid="seats-progress"
          style={{
            height: "100%",
            width: `${pct}%`,
            background: "#b8c975",
            transition: "width 800ms cubic-bezier(0.2, 0.7, 0.2, 1)",
          }}
        />
      </div>
    </div>
  );
}
